import { InputAdornment, Slider, type SliderProps, Stack, TextField, type TextFieldProps, Typography } from '@mui/material';
import { type ChangeEvent, memo, type ReactNode, useCallback, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

type SliderWithInputProps = Readonly<{
  value: number;
  onChange: (value: number) => void;
  color: SliderProps['color'];
  inputWidth?: number | string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  marks?: readonly number[];
  endAdornment?: ReactNode;
  decimalScale?: number;
  railStyle?: 'gradient' | 'filled';
  pl?: number;
}>;

const DRAG_ICON_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='rgba(0,0,0,0.3)' d='M20 9H4v2h16V9zM4 15h16v-2H4v2z'/%3E%3C/svg%3E")`;

const DEFAULT_MARKS = [0, 25, 50, 75, 100];

export const SliderWithInput = memo(function SliderWithInput({
  color,
  onChange,
  value,
  inputWidth,
  min = 0,
  max = 100,
  step = 1,
  unit = '%',
  marks = DEFAULT_MARKS,
  endAdornment: customEndAdornment,
  decimalScale,
  railStyle = 'gradient',
  pl = 1.25,
}: SliderWithInputProps) {
  const handleSliderChange: Required<SliderProps>['onChange'] = useCallback(
    (_, newValue) => {
      if (typeof newValue !== 'number') {
        return;
      }
      onChange(newValue);
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(!event.target.value ? min : Number(event.target.value));
    },
    [min, onChange]
  );

  const sliderMarks = useMemo(() => {
    return marks.map((mark) => ({ value: mark }));
  }, [marks]);

  const sliderSx = useMemo(
    () => ({
      px: '1px',
      margin: 'auto 8px auto 0 !important',
      '& .MuiSlider-rail': {
        backgroundColor: `var(--mui-palette-${color}-_states-selected)`,
        boxShadow: 'var(--mui-palette-common-shadows-perplInnerOuterShadow)',
      },
      '& .MuiSlider-thumb': {
        boxShadow: `var(--mui-palette-common-shadows-thumbInnerShadow)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(90deg)',
          width: '16px',
          height: '16px',
          backgroundImage: DRAG_ICON_SVG,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'contain',
          boxShadow: 'none',
        },
        height: '16px',
        width: '16px',
        borderRadius: '4px',
        '&:hover': {
          boxShadow:
            '0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12), ' +
            '1px 1px 2px 0px rgba(255, 255, 255, 0.12) inset, -1px -1px 1px 0px rgba(0, 0, 0, 0.16) inset',
        },
        '&.Mui-active': {
          boxShadow:
            '0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12), ' +
            '1px 1px 2px 0px rgba(255, 255, 255, 0.12) inset, -1px -1px 1px 0px rgba(0, 0, 0, 0.16) inset',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(90deg)',
            width: '16px',
            height: '16px',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='rgba(0,0,0,0.3)' d='M20 9H4v2h16V9zM4 15h16v-2H4v2z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            backgroundColor: `var(--mui-palette-${color}-main)`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '24px',
            height: '24px',
            borderRadius: '8px',
            backgroundColor: `var(--mui-palette-${color}-main)`,
            opacity: 0.16,
            zIndex: -1,
          },
        },
      },
      '& .MuiSlider-track': {
        background:
          railStyle === 'filled'
            ? `var(--mui-palette-${color}-main)`
            : `linear-gradient(to right, color-mix(in srgb, var(--mui-palette-${color}-main) 40%, transparent), var(--mui-palette-${color}-main))`,
        border: 'none',
        boxShadow: 'var(--mui-palette-common-shadows-perplInnerShadow)',
      },
      '& .MuiSlider-markLabel': {
        fontSize: '0.625rem',
      },
      [`& .MuiSlider-markLabel[data-index="${marks.length - 1}"]`]: {
        transform: 'translateX(-90%)',
      },
      [`& .MuiSlider-markLabel[data-index="0"]`]: {
        transform: 'translateX(0%)',
      },
      '& .MuiSlider-markLabelActive': {
        fontSize: '0.625rem',
        color: `var(--mui-palette-${color}-main)`,
      },
      '& .MuiSlider-mark': {
        opacity: 0.3,
        '&[data-index="0"]': {
          left: '2px !important',
        },
      },
      '& .MuiSlider-markActive': {
        opacity: 0.8,
      },
    }),
    [color, railStyle, marks.length]
  );

  const textFieldSx = useMemo(
    () => ({
      flex: `1 0 ${inputWidth ?? '4rem'}`,
    }),
    [inputWidth]
  );

  const isAllowed = useCallback(
    (values: { floatValue?: number }) => {
      const { floatValue } = values;
      if (floatValue == null) {
        return true;
      }
      return floatValue >= min && floatValue <= max;
    },
    [min, max]
  );

  const endAdornment = useMemo(
    () => (
      <InputAdornment position="end" sx={{ ml: 0.5 }}>
        {customEndAdornment ?? <Typography variant="body2">{unit}</Typography>}
      </InputAdornment>
    ),
    [unit, customEndAdornment]
  );

  const slotProps = useMemo(
    () => ({
      htmlInput: {
        // Text input (see NumberInputField): type="number" breaks NumericFormat's caret
        // restoration. Bounds come from isAllowed, so min/max/step aren't needed here.
        inputMode: 'decimal' as const,
        sx: {
          px: 0,
          textAlign: 'right' as const,
        },
        className: 'disable_number_arrow',
      },
      input: {
        endAdornment,
      },
    }),
    [endAdornment]
  );

  return (
    <Stack direction="row" spacing={1.5} pl={pl}>
      <Slider
        marks={sliderMarks}
        value={value ?? min}
        step={step}
        min={min}
        max={max}
        size="medium"
        color={color}
        valueLabelDisplay="off"
        onChange={handleSliderChange}
        sx={sliderSx}
      />
      <NumericFormat<TextFieldProps<'outlined'>>
        value={value}
        customInput={TextField}
        variant="outlined"
        size="small"
        sx={textFieldSx}
        isAllowed={isAllowed}
        decimalScale={decimalScale}
        slotProps={slotProps}
        onChange={handleInputChange}
      />
    </Stack>
  );
});
